/* Native, instance-scoped story controls. Short video sources/preload/autoplay
   are in the initial HTML, not deferred here. This script handles safe playback
   policy and scroll-snap controls without changing cart/gallery/review handlers. */
(() => {
  'use strict';

  class LkStoryPlayer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.auto = this.dataset.lkStoryAutoplay === 'true';
      this.userPaused = false;
      this.pendingPolicyPauses = 0;
      if (this.auto) this.video.defaultMuted = true;

      this.video.addEventListener('pause', () => {
        if (this.pendingPolicyPauses > 0) {
          this.pendingPolicyPauses -= 1;
          return;
        }
        if (!document.hidden && !this.motion.matches) this.userPaused = true;
      }, {signal});
      this.video.addEventListener('play', () => { this.userPaused = false; }, {signal});
      this.sync = () => {
        if (document.hidden || this.motion.matches) {
          this.video.autoplay = false;
          this.pauseByPolicy();
        } else if (this.auto && !this.userPaused) {
          this.video.autoplay = true;
          const playing = this.video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(() => {});
          // Autoplay rejection leaves native Play available; no forced retries.
        }
      };
      document.addEventListener('visibilitychange', this.sync, {signal});
      this.motion.addEventListener('change', this.sync, {signal});
      window.addEventListener('pagehide', () => this.pauseByPolicy(), {signal});
      window.addEventListener('pageshow', this.sync, {signal});
      this.sync();
    }

    pauseByPolicy() {
      if (!this.video.paused) {
        this.pendingPolicyPauses += 1;
        this.video.pause();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.pauseByPolicy();
      this.controller = null;
    }
  }

  class LkStoryCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-lk-story-track]');
      this.controls = this.querySelector('[data-lk-story-controls]');
      if (!this.track || !this.controls) return;
      this.slides = [...this.track.querySelectorAll('[data-lk-story-slide]')];
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-lk-story-step]');
        if (button && !button.disabled) this.move(this.currentIndex() + Number(button.dataset.lkStoryStep));
      }, {signal});
      this.track.addEventListener('scroll', this.schedule, {passive: true, signal});
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        let index = this.currentIndex();
        if (event.key === 'ArrowRight') index += rtl ? -1 : 1;
        else if (event.key === 'ArrowLeft') index += rtl ? 1 : -1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.slides.length - 1;
        else return;
        event.preventDefault();
        this.move(index);
      }, {signal});
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-lk-story-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.move(index, true);
      }, {signal});
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.schedule, {signal});
      }
      this.schedule();
    }

    currentIndex() {
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      const start = this.track.getBoundingClientRect()[edge];
      let nearest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const nextDistance = Math.abs(slide.getBoundingClientRect()[edge] - start);
        if (nextDistance < distance) { distance = nextDistance; nearest = index; }
      });
      return nearest;
    }

    move(index, immediate = false) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (!slide) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const edge = rtl ? 'right' : 'left';
      const delta = slide.getBoundingClientRect()[edge] - this.track.getBoundingClientRect()[edge];
      // Geometry works in LTR and every browser's RTL scrollLeft model. Scroll
      // only this track, not scrollIntoView() ancestors/the PDP viewport.
      this.track.scrollBy({left: delta, behavior: immediate || this.motion.matches ? 'auto' : 'smooth'});
    }

    update() {
      const overflow = this.slides.length > 1 && this.track.scrollWidth > this.track.clientWidth + 2;
      this.controls.hidden = !overflow;
      if (!overflow) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const track = this.track.getBoundingClientRect();
      const first = this.slides[0].getBoundingClientRect();
      const last = this.slides[this.slides.length - 1].getBoundingClientRect();
      this.controls.querySelector('[data-lk-story-step="-1"]').disabled = rtl ? first.right <= track.right + 2 : first.left >= track.left - 2;
      this.controls.querySelector('[data-lk-story-step="1"]').disabled = rtl ? last.left >= track.left - 2 : last.right <= track.right + 2;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }

  if (!customElements.get('lk-story-player')) customElements.define('lk-story-player', LkStoryPlayer);
  if (!customElements.get('lk-story-carousel')) customElements.define('lk-story-carousel', LkStoryCarousel);
})();